import { IOCGroupedByFileType, IOCPrevalenceVerdicts } from '../types'
import { runVerdictsUpdater } from '../features/ioc/helpers'

/**
 * Mixin to handle IOC verdict updates with
 * - group verdicts,
 * - whitelist/blacklist,
 * - prevalence (optional)
 */
export function UpdatedIOCsMixin<T extends new (...args: any[]) => any>(Base: T) {
  return class extends Base {
    private cachedUpdatedIOCs: IOCGroupedByFileType | undefined
    private lastIocsData: any
    private lastGroupVerdicts: any
    private lastReportVerdict: any

    /**
     * Get IOCs with updated verdicts based on group verdicts, whitelist/blacklist, and optionally prevalence
     * Results without prevalence are memoized for the lifetime of the wrapper instance
     *
     * @param reportId - Report ID
     * @param prevalenceVerdicts - Optional prevalence verdicts to include
     * @returns Updated IOCs with recalculated verdicts
     */
    getUpdatedIOCs(reportId: string, prevalenceVerdicts?: IOCPrevalenceVerdicts): IOCGroupedByFileType | undefined {
      const iocs = this.getIOCs(reportId)
      if (!iocs?.data) {
        return undefined
      }

      const groupVerdicts = this.getGroupVerdicts(reportId)
      const reportVerdict = this.getVerdict(reportId)

      // If no prevalence, check cache first
      if (
        !prevalenceVerdicts &&
        this.cachedUpdatedIOCs &&
        this.lastIocsData === iocs.data &&
        this.lastGroupVerdicts === groupVerdicts &&
        this.lastReportVerdict === reportVerdict
      ) {
        return this.cachedUpdatedIOCs
      }

      const [updatedIocs] = runVerdictsUpdater(iocs.data, groupVerdicts, prevalenceVerdicts, reportVerdict)

      // Cache result only if no prevalence was used (common case)
      if (!prevalenceVerdicts) {
        this.cachedUpdatedIOCs = updatedIocs
        this.lastIocsData = iocs.data
        this.lastGroupVerdicts = groupVerdicts
        this.lastReportVerdict = reportVerdict
      }

      return updatedIocs
    }
  }
}
